<template>
  <div class="app-breadcrumbs">
    <template
      v-for="(breadcrumb, breadcrumbIndex) in breadcrumbs"
      :key="`separator-${breadcrumbIndex}`"
    >
      <div class="app-breadcrumb">
        <Tooltip v-if="breadcrumb.tooltip" placement="bottom">
          <template #trigger>
            <a
              v-if="breadcrumb.href"
              :href="breadcrumb.href"
              class="breadcrumb-name"
              :class="{ shorter: shorterBreadcrumbs }"
            >
              {{ breadcrumb.name }}
            </a>
            <router-link
              v-else-if="breadcrumb.to"
              :to="breadcrumb.to"
              class="breadcrumb-name"
              :class="{ shorter: shorterBreadcrumbs }"
            >
              {{ breadcrumb.name }}
            </router-link>
            <template v-else>
              <div
                class="breadcrumb-name"
                :class="{
                  shorter: shorterBreadcrumbs,
                  'not-last': breadcrumbIndex !== breadcrumbs.length - 1
                }"
              >
                {{ breadcrumb.name }}
              </div>
            </template>
          </template>
          {{ breadcrumb.tooltip }}
        </Tooltip>
        <template v-else>
          <div
            class="breadcrumb-name"
            :class="{ 'not-last': breadcrumbIndex !== breadcrumbs.length - 1 }"
          >
            {{ breadcrumb.name }}
          </div>
        </template>
      </div>
      <div v-if="breadcrumbIndex < breadcrumbs.length - 1" class="app-breadcrumb-separator">/</div>
    </template>
  </div>
</template>

<script lang="ts" setup>
  /* eslint-disable @typescript-eslint/restrict-template-expressions */
  import { storeToRefs } from 'pinia'
  import { computed } from 'vue'
  import { useRoute } from 'vue-router'

  import { Tooltip } from '@/components/ui'
  import { getDayDisplayName } from '@/const'
  import { useDietsStore } from '@/store/dietsStore'

  const beDomain = import.meta.env.VUE_APP_API_HOST
  // React routes (patients, etc.) - use current origin so proxy (8005) links work
  const appBase = typeof window !== 'undefined' ? window.location.origin : beDomain

  const route = useRoute()
  const dietStore = useDietsStore()
  const { diet, patientId, patientName } = storeToRefs(dietStore)
  const dietName = computed(() => diet.value?.data.name ?? '')

  const dietGroupName = computed(() => {
    if (diet.value && route.params.dietType !== 'patient' && route.params.dietType !== 'system') {
      if ('group_name' in diet.value) {
        return diet.value.group_name ?? 'Katalog główny'
      }
    }
    return ''
  })
  const shorterBreadcrumbs = computed(() => {
    return route.params.dietType === 'patient'
  })
  const breadcrumbs = computed(() => {
    const breadcrumbsList: Breadcrumbs[] = []

    if (route.path === '/dashboard') {
      return [
        {
          name: 'Dashboard',
        },
      ]
    }
    if (route.matched.some(v => v.name?.toString().includes('IngredientsList'))) {
      breadcrumbsList.push({ name: 'Produkty' })
    }
    if (route.matched.some(v => v.name?.toString().includes('DietList'))) {
      breadcrumbsList.push({ name: 'Szablony' })
    }
    if (route.matched.some(v => v.name?.toString().includes('DishesList'))) {
      breadcrumbsList.push({ name: 'Potrawy' })
    }
    if (route.matched.some(v => v.name === 'DietDetail')) {
      if (route.params.dietType === 'patient') {
        breadcrumbsList.push({
          href: `${appBase}/patients`,
          name: 'Pacjenci',
          tooltip: 'Przejdź do listy Pacjentów',
        })
        breadcrumbsList.push({
          href: `${appBase}/patients/${patientId.value}`,
          name: patientName.value ?? '',
          tooltip: 'Przejdź do zakładki Pacjenta',
        })
        breadcrumbsList.push({
          href: `${appBase}/patients/${patientId.value}/meal-plans`,
          name: 'Jadłospisy',
          tooltip: 'Przejdź do listy jadłospisów Pacjenta',
        })
      } else {
        breadcrumbsList.push({
          name: 'Jadłospisy',
          to: {
            path: `/diet/${route.params.dietType}`,
          },
          tooltip: 'Przejdź do listy jadłospisów',
        })
      }
      if (dietGroupName.value) {
        breadcrumbsList.push({
          name: dietGroupName.value,
          tooltip: dietGroupName.value,
        })
      }
      if (dietName.value) {
        if (route.name === 'DietDetailDay') {
          breadcrumbsList.push({
            name: dietName.value,
            to: {
              name: 'DietDetailWeek',
              params: {
                dietId: route.params.dietId,
                dietType: route.params.dietType,
              },
            },
            tooltip: 'Przejdź do widoku tygodniowego',
          })
          breadcrumbsList.push({
            name: getDayDisplayName(
              parseInt(route.params.dayIndex as string),
              diet.value?.data.diet_type,
              diet.value?.data.start_date,
            ),
          })
        } else {
          breadcrumbsList.push({
            name: dietName.value,
            tooltip: dietName.value,
          })
        }
      }
      if (route.name === '') {
        breadcrumbsList.push({
          name: 'Dashboard',
        })
      }
    }
    if (route.name === 'Chat') {
      breadcrumbsList.push({
        name: 'Chat',
      })
    }

    return breadcrumbsList
  })
</script>

<style>
  .app-breadcrumbs {
    display: flex;
    align-items: center;
    font-family: Montserrat-Medium;
    font-size: 14px;
  }

  .app-content-header .app-breadcrumbs {
    overflow: inherit;
  }

  .app-breadcrumb a {
    display: block;
    color: var(--color-tertiary-text);
    text-decoration: none;
  }

  .app-breadcrumb .breadcrumb-name {
    max-width: 400px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .app-breadcrumb .breadcrumb-name.not-last {
    color: var(--color-tertiary-text);
  }

  @media (width <= 1366px) {
    .app-breadcrumb .breadcrumb-name.shorter {
      max-width: 105px;
    }
  }

  @media (width >= 1367px) {
    .app-breadcrumb .breadcrumb-name {
      max-width: 310px;
    }

    .app-breadcrumb .breadcrumb-name.shorter {
      max-width: 150px;
    }
  }

  @media (width >= 1536px) {
    .app-breadcrumb .breadcrumb-name {
      max-width: 400px;
    }
  }

  .app-breadcrumb-separator {
    margin: 0 6px;
    color: var(--color-tertiary-text);
  }

  .app-breadcrumb-separator:nth-last-child(2) {
    color: var(--color-primary-text);
  }
</style>
